<template>
    <ion-page>
      <!-- 顶部栏 -->
      <ion-header>
        <ion-toolbar>
          <ion-buttons slot="start">
            <ion-back-button @click="router.go(-1)" color="dark"></ion-back-button>
          </ion-buttons>
          <ion-title>{{ $t('NewFriends') || 'New Friends' }}</ion-title>
        </ion-toolbar>
      </ion-header>
  
      <ion-content :fullscreen="true" class="friend-requests-content">
        <!-- Add Friend Section - 吸顶 -->
        <div class="add-friend-sticky">
          <div class="add-friend-section">
            <div class="section-header">
              <ion-icon :icon="personAddOutline" class="section-icon"></ion-icon>
              <h2>{{ $t('AddFriend') || 'Add Friend' }}</h2>
            </div>
            
            <!-- Input with Search Button -->
            <div class="input-search-container">
              <ion-item class="search-input-item">
                <ion-input
                  v-model="friendPub"
                  placeholder="Enter TalkFlow ID..."
                  @ionInput="friendPub = String($event.target.value)"
                  class="friend-input"
                ></ion-input>
                <ion-button 
                  slot="end" 
                  fill="solid" 
                  @click="searchUser"
                  :disabled="!friendPub.trim()"
                  class="search-button"
                >
                  <ion-icon :icon="searchOutline" slot="icon-only"></ion-icon>
                </ion-button>
              </ion-item>
            </div>
  
            <!-- Action Buttons Row -->
            <div class="action-buttons">
              <ion-button fill="outline" @click="gotoscanner" class="qr-scan-button">
                <ion-icon :icon="scanOutline" slot="start"></ion-icon>
                {{ $t('ScanQR') || 'Scan QR' }}
              </ion-button>
            </div>
  
            <!-- Error Message -->
            <div v-if="buddyError" class="error-message">
              <ion-icon :icon="alertCircleOutline" class="error-icon"></ion-icon>
              <ion-text color="danger">{{ buddyError }}</ion-text>
            </div>
          </div>
        </div>
  
        <!-- Friend Requests Section -->
        <div class="requests-section">
          <div class="section-header">
            <ion-icon :icon="earthOutline" class="section-icon"></ion-icon>
            <h2>{{ $t('FriendRequests') || 'Friend Requests' }}</h2>
            <ion-badge v-if="filteredReceivedRequests.length > 0" color="primary" class="count-badge">
              {{ filteredReceivedRequests.length }}
            </ion-badge>
          </div>
  
          <div class="requests-container">
            <ion-list class="requests-list">
              <ion-item
                v-for="(req, idx) in filteredReceivedRequests"
                :key="req.from + idx"
                button
                @click="openRequestModal(req)"
                class="request-item"
                lines="none"
              >
                <ion-avatar slot="start" v-if="strangerAvatars[req.from]">
                  <img :src="strangerAvatars[req.from]" />
                </ion-avatar>
                <ion-avatar slot="start" v-else>
                  <img :src="getGunAvatar(req.from)" alt="Avatar" />
                </ion-avatar>
                <ion-label>
                  <h2>{{ strangerAliases[req.from] || 'Loading...' }}</h2>
                  <p class="pub-key" @click.stop="copyPubWithFeedback(req.from)">
                    {{ $t('talkflowid') }}: {{ truncatePub(req.from) }}
                  </p>
                  <p v-if="req.message" class="message">{{ $t('message') }}: {{ req.message }}</p>
                </ion-label>
    
              </ion-item>
  
              <!-- Empty State -->
              <div v-if="!filteredReceivedRequests.length" class="empty-state">
                <div class="empty-illustration">
                  <ion-icon :icon="peopleOutline" class="empty-icon"></ion-icon>
                </div>
                <p class="empty-text">{{ $t('noRequests') || 'No friend requests' }}</p>
                <p class="empty-subtext">{{ $t('noRequestsDesc') || 'New friend requests will appear here' }}</p>
              </div>
            </ion-list>
          </div>
        </div>
  
        <!-- Request Details Modal (Bottom Sheet) -->
        <ion-modal
          :is-open="isModalOpen"
          class="request-modal"
          :breakpoints="[0, 0.5, 0.8]"
          :initial-breakpoint="0.5"
          @didDismiss="closeRequestModal"
        >
          <ion-content class="modal-content">
            <div class="modal-header">
              <ion-avatar v-if="selectedRequest?.avatar" class="modal-avatar">
                <img :src="selectedRequest.avatar" />
              </ion-avatar>
              <ion-avatar v-else-if="selectedRequest?.from" class="modal-avatar">
                <img :src="getGunAvatar(selectedRequest.from)" alt="Avatar" />
              </ion-avatar>
              <div class="header-text">
                <h2>{{ selectedRequest?.alias || 'Loading...' }}</h2>
              </div>
              <ion-button fill="clear" @click="closeRequestModal" class="close-button">
                <ion-icon color="dark" :icon="closeOutline" slot="icon-only"></ion-icon>
              </ion-button>
            </div>
            <div class="modal-body">
              <ion-item lines="none">
                <ion-label position="stacked">PubKey</ion-label>
                <div class="pub-key-container">
                  <ion-text>{{ selectedRequest?.from || '' }}</ion-text>
                </div>
              </ion-item>
              <ion-item lines="none" v-if="selectedRequest?.message">
                <ion-label position="stacked">Message</ion-label>
                <ion-text class="message-text">{{ selectedRequest.message }}</ion-text>
              </ion-item>
              <ion-item lines="none">
                <ion-label>{{ $t('addblacklist') }}</ion-label>
                <ion-toggle slot="end" v-model="isBlocked" @ionChange="toggleBlacklist"></ion-toggle>
              </ion-item>
            </div>
            <div class="modal-actions">
              <ion-button expand="block" color="success" @click="handleAcceptRequest">
                {{ $t('agree') }}
              </ion-button>
              <ion-button expand="block" fill="outline" color="danger" @click="handleRejectRequest">
                {{ $t('refuse') }}
              </ion-button>
            </div>
          </ion-content>
        </ion-modal>
      </ion-content>
    </ion-page>
  </template>
  
  <script setup lang="ts">
  import { ref, computed, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import {
    IonPage, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent,
    IonItem, IonLabel, IonIcon, IonAvatar, IonModal, IonInput, IonToggle,
    IonText, IonBadge, IonList, IonBackButton
  } from '@ionic/vue';
  
  import { 
    arrowBackOutline, personAddOutline, scanOutline, searchOutline, earthOutline, 
    closeOutline, peopleOutline, alertCircleOutline, chevronForwardOutline 
  } from 'ionicons/icons';
  import { getTalkFlowCore } from '@/composables/TalkFlowCore';
  import { gunAvatar, mountClass } from "gun-avatar";
  import { toastController } from '@ionic/vue';
  
  mountClass();
  
  // Define ReceivedRequest type
  interface ReceivedRequest {
    from: string;
    message?: string;
  }
  
  const chatFlowStore = getTalkFlowCore();
  const {
    buddyList, receivedRequests, friendPub, buddyError, searchUserProfile,
    gun, acceptBuddyRequest, rejectBuddyRequest, addToBlacklist, removeFromBlacklist, 
    isInMyBlacklist, storageServ, copyPub
  } = chatFlowStore;
  
  const router = useRouter();
  
  // State for stranger data (for friend requests)
  const strangerAliases = ref<Record<string, string>>({});
  const strangerAvatars = ref<Record<string, string>>({});
  
  // Modal state for friend requests
  const isModalOpen = ref(false);
  const selectedRequest = ref<{ from: string; message?: string; alias?: string; avatar?: string } | null>(null);
  const isBlocked = ref(false);
  
  // Filter out already-added friends
  const filteredReceivedRequests = computed(() => {
    return receivedRequests.value.filter(req => !buddyList.value.some(b => b.pub === req.from));
  });
  
  // Fetch stranger data from Gun.js
  function fetchStrangerData(pub: string) {
    gun.get('users').get(pub).once((data: any) => {
      if (data) {
        strangerAliases.value[pub] = data.alias || 'No Name';
        strangerAvatars.value[pub] = data.avatar || '';
        console.log(`Fetched data for ${pub}:`, { alias: data.alias, avatar: data.avatar });
      } else {
        strangerAliases.value[pub] = 'No Name';
        strangerAvatars.value[pub] = '';
        console.log(`No data found for ${pub}`);
      }
    });
  }
  
  // Truncate public key for list display
  function truncatePub(pub: string): string {
    return pub.length > 20 ? `${pub.slice(0, 8)}...${pub.slice(-8)}` : pub;
  }
  
  // Copy with feedback
  async function copyPubWithFeedback(pub: string) {
    await copyPub(pub);
    const toast = await toastController.create({
      message: 'ID Copied',
      duration: 1500,
      position: 'bottom',
      color: 'dark',
    });
    await toast.present();
  }
  
  // Open request modal
  function openRequestModal(req: ReceivedRequest) {
    console.log('Opening modal for request:', req);
    selectedRequest.value = {
      ...req,
      alias: strangerAliases.value[req.from],
      avatar: strangerAvatars.value[req.from],
    };
    isBlocked.value = isInMyBlacklist(req.from);
    isModalOpen.value = true;
    console.log('Modal state after opening:', { isModalOpen: isModalOpen.value, selectedRequest: selectedRequest.value });
  }
  
  // Close request modal
  function closeRequestModal() {
    console.log('Closing modal');
    isModalOpen.value = false;
    selectedRequest.value = null;
  }
  
  // Toggle blacklist
  async function toggleBlacklist() {
    if (!selectedRequest.value) return;
    console.log('Toggling blacklist for:', selectedRequest.value.from, 'New state:', isBlocked.value);
    if (isBlocked.value) {
      addToBlacklist(selectedRequest.value.from);
      await storageServ.saveBlacklist(selectedRequest.value.from, true);
    } else {
      removeFromBlacklist(selectedRequest.value.from);
      await storageServ.saveBlacklist(selectedRequest.value.from, false);
    }
  }
  
  // Handle accept request
  function handleAcceptRequest() {
    if (selectedRequest.value?.from) {
      console.log('Accepting request for:', selectedRequest.value.from);
      acceptBuddyRequest(selectedRequest.value.from);
      closeRequestModal();
    }
  }
  
  // Handle reject request
  function handleRejectRequest() {
    if (selectedRequest.value?.from) {
      console.log('Rejecting request for:', selectedRequest.value.from);
      rejectBuddyRequest(selectedRequest.value.from);
      closeRequestModal();
    }
  }
  
  // Navigation functions
  function goBack() {
    router.back();
  }
  
  function gotoscanner() {
    router.push('ScanPage');
  }
  
  async function searchUser() {
    const pubRaw = friendPub.value.trim();
  
    if (!pubRaw) {
      buddyError.value = 'Publickey is null';
      return;
    }
  
    const pub = pubRaw.replace('pubkey:', '');
    console.log('🔍 智能搜索路由，目标 pub:', pub.slice(0, 8));
  
    // 🎯 立即检查本地通讯录（无网络延迟）
    const isExistingFriend = buddyList.value.some(b => b.pub === pub);
  
    if (isExistingFriend) {
      // ✅ 已是好友 → 直接进入好友主页
      console.log('📱 好友已存在，导航到好友主页');
      router.push({ path: '/friend-profile', query: { pub } });
    } else {
      // 🆕 陌生人 → 进入陌生人主页，开始数据同步
      console.log('🔍 陌生人检测，导航到陌生人主页并启动数据同步');
      router.push({ path: '/stranger-profile', query: { pub } });
    }
  
    // 清理输入状态
    friendPub.value = '';
    buddyError.value = '';
    
    // 📡 后台启动数据同步（无论是否为好友，都尝试更新数据）
    startBackgroundSync(pub);
  }
  
  // 🔄 后台数据同步函数
  function startBackgroundSync(pub: string) {
    console.log(`🔄 启动后台数据同步: ${pub.slice(0, 8)}`);
    
    // 非阻塞式数据获取
    setTimeout(() => {
      gun.get('users').get(pub).once((data: any) => {
        if (data) {
          console.log(`📥 后台同步成功: ${pub.slice(0, 8)}`, {
            alias: data.alias,
            hasAvatar: !!data.avatar,
            hasSignature: !!data.signature,
            hasEpub: !!data.epub
          });
        } else {
          console.log(`📭 后台同步无数据: ${pub.slice(0, 8)}`);
        }
      });
    }, 100); // 延迟100ms避免阻塞UI
  }
  
  // Theme support
  import { useTheme } from '@/composables/useTheme';
  const { isDark } = useTheme();
  
  const getGunAvatar = (pub: string) => {
    return gunAvatar({
      pub: pub,
      round: false,
      size: 200,
      dark: isDark.value,
      svg: true
    });
  };
  
  onMounted(() => {
    // Initialize stranger data for friend requests
    filteredReceivedRequests.value.forEach(req => {
      fetchStrangerData(req.from);
    });
  });
  
  </script>
  
  <style scoped>
  
  /* 吸顶的Add Friend区域 */
  .add-friend-sticky {
    position: sticky;
    top: 0;
    z-index: 100;
    background: var(--ion-background-color);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    box-shadow: 0 2px 16px rgba(0, 0, 0, 0.1);
  }
  
  /* Add Friend Section */
  .add-friend-section {
    padding: 16px;
    background: transparent;
  }
  
  /* Section Headers */
  .section-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    padding: 0 4px;
  }
  
  .section-icon {
    font-size: 24px;
    margin-right: 12px;
    color: var(--ion-color-primary);
  }
  
  .section-header h2 {
    font-size: 20px;
    font-weight: 600;
    color: var(--ion-text-color);
    margin: 0;
    flex: 1;
  }
  
  .count-badge {
    margin-left: 8px;
    --background: var(--ion-color-primary);
  }
  
  /* 输入搜索容器 */
  .input-search-container {
    margin-bottom: 16px;
    border: none;
  }
  
  .search-input-item {
    --background: rgba(255, 255, 255, 0.1);
    --border-radius: 10px;
    --padding-start: 20px;
    --padding-end: 8px;
    --inner-padding-end: 0;
    --border-width: 0;
    border: none;
  }
  
  .friend-input {
    flex: 1;
    font-size: 16px;
    --placeholder-color: var(--ion-color-medium);
    border: none;
  }
  
  .search-button {
    --border-radius: 10px;
    height: 44px;
    --background: var(--ion-color-primary);
    --box-shadow: 0 4px 12px rgba(var(--ion-color-primary-rgb), 0.3);
    border: none;
  }
  
  /* 操作按钮区域 */
  .action-buttons {
    display: flex;
    gap: 12px;
    margin-bottom: 8px;
  }
  
  .qr-scan-button {
    flex: 1;
    --border-radius: 12px;
    height: 44px;
    --border-width: 2px;
    --border-color: var(--ion-color-primary);
    --color: var(--ion-color-primary);
  }
  
  /* 错误消息样式 */
  .error-message {
    display: flex;
    align-items: center;
    margin: 8px 4px 0;
    padding: 12px 16px;
    background: rgba(255, 0, 0, 0.1);
    border: 1px solid rgba(255, 0, 0, 0.2);
    border-radius: 12px;
    backdrop-filter: blur(10px);
  }
  
  .error-icon {
    font-size: 20px;
    margin-right: 8px;
    color: var(--ion-color-danger);
  }
  
  /* Requests Section */
  .requests-section {
    padding: 24px 16px 32px;
    background: transparent;
  }
  
  .requests-container {
    background: transparent;
  }
  
  .requests-list {
    background: transparent;
    padding: 0;
  }
  
  .request-item {
    --background: rgba(255, 255, 255, 0.08);
    --border-radius: 16px;
    --padding-start: 20px;
    --padding-end: 20px;
    --inner-padding-end: 0;
    --min-height: 80px;
    margin-bottom: 12px;
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    transition: all 0.3s ease;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }
  
  .request-item:hover {
    --background: rgba(255, 255, 255, 0.12);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  
  .request-item ion-avatar {
    width: 56px;
    height: 56px;
    margin-right: 16px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  .request-item ion-label {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  
  .request-item h2 {
    font-size: 18px;
    font-weight: 600;
    color: var(--ion-text-color, #000);
    margin: 0 0 6px 0;
  }
  
  .request-item .pub-key {
    font-size: 13px;
    color: var(--ion-color-step-600, #666);
    margin: 0 0 4px 0;
    cursor: pointer;
    transition: color 0.2s ease;
  }
  
  .request-item .pub-key:hover {
    color: var(--ion-color-primary);
  }
  
  
  
  .request-item .message {
    font-size: 13px;
    color: var(--ion-color-step-600, #666);
    margin: 0;
    font-style: italic;
  }
  
  .forward-icon {
    color: var(--ion-color-step-400, #999);
    font-size: 20px;
  }
  
  /* Empty State */
  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: var(--ion-color-step-600, #666);
  }
  
  .empty-illustration {
    margin-bottom: 24px;
  }
  
  .empty-icon {
    font-size: 80px;
    color: var(--ion-color-step-400, #999);
    opacity: 0.6;
  }
  
  .empty-text {
    font-size: 20px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: var(--ion-text-color);
  }
  
  .empty-subtext {
    font-size: 15px;
    margin: 0;
    opacity: 0.7;
    line-height: 1.4;
  }
  
  /* Request Modal */
  .request-modal {
    --background: var(--ion-background-color, #fff);
    --border-radius: 16px 16px 0 0;
  }
  
  .modal-content {
    padding: 20px;
  }
  
  .modal-header {
    display: flex;
    align-items: center;
    gap: 15px;
    margin: 10px;
  }
  
  .modal-avatar {
    width: 56px;
    height: 56px;
    border: 2px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
  
  .header-text {
    flex: 1;
  }
  
  .modal-header h2 {
    font-size: 20px;
    font-weight: 600;
    color: var(--ion-text-color, #000);
    margin: 0;
  }
  
  .close-button {
    --padding-start: 0;
    --padding-end: 0;
    --border-radius: 50%;
  }
  
  .modal-body {
    margin: 10px;
  }
  
  .modal-body ion-item {
    --background: transparent;
    --border-width: 0;
    --padding-start: 0;
    --inner-padding-end: 0;
    margin-bottom: 15px;
  }
  
  .modal-body ion-label {
    color: var(--ion-color-step-600, #666);
    font-weight: 500;
    margin-bottom: 5px;
  }
  
  .modal-body ion-text {
    font-size: 14px;
    color: var(--ion-text-color, #000);
  }
  
  .modal-body .pub-key-container {
    max-height: 100px;
    overflow-y: auto;
    word-break: break-all;
    padding: 12px;
    background: var(--ion-color-step-100, #f7f7f7);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .modal-body .message-text {
    white-space: pre-wrap;
    padding: 12px;
    background: var(--ion-color-step-100, #f7f7f7);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
  
  .modal-body ion-toggle {
    --background: var(--ion-color-step-300, #ddd);
    --background-checked: var(--ion-color-primary, #3880ff);
    --handle-background: #fff;
    --handle-background-checked: #fff;
  }
  
  .modal-actions {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 20px 10px 0;
  }
  
  .modal-actions ion-button {
    --border-radius: 12px;
    height: 48px;
    font-weight: 600;
  }
  
  /* Responsive Design */
  @media (max-width: 768px) {
    .add-friend-section {
      padding: 12px;
    }
    
    .section-header h2 {
      font-size: 18px;
    }
    
    .search-button {
      --padding-start: 12px;
      --padding-end: 12px;
    }
    
    .requests-section {
      padding: 20px 12px 32px;
    }
  }
  
  /* Dark mode optimizations */
  @media (prefers-color-scheme: dark) {
    .add-friend-sticky {
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .search-input-item {
      border: none;
    }
    
    .request-item {
      border: 1px solid rgba(255, 255, 255, 0.05);
    }
  }
  </style>